import type { ApAgingResponse } from '@dcc-erp/shared';
import { fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { exportRows, todayStamp, type ExportColumn } from '../../lib/xlsx';

// =============================================================================
// Aging tab — per-vendor 5-bucket AP aging from useApAging (server already
// excludes VOID/PENDING). Bucket labels mirror ap-aging.service: notDue / 1-30 /
// 31-60 / 61-90 / 90+.
//
// WS9 — extracted verbatim from the ap.tsx route monolith (pilot decomposition).
// Purely presentational: it takes the aging response + companyCode as props, so
// the /ap route owns the useApAging fetch and this stays render-only. The
// existing ap.aging-payments.test.tsx mounts <ApPage/> and clicks the อายุหนี้
// tab, so it guards this component's compose-into-route wiring.
// =============================================================================
export interface ApAgingTabProps {
  aging: ApAgingResponse | undefined;
  companyCode: string;
}

export function ApAgingTab({ aging, companyCode }: ApAgingTabProps) {
  const vendors = aging?.vendors ?? [];

  async function handleExport() {
    const cols: ExportColumn<ApAgingResponse['vendors'][number]>[] = [
      { header: 'vendorCode', value: (v) => v.vendorCode, width: 12 },
      { header: 'vendorName', value: (v) => v.vendorName, width: 30 },
      { header: 'notDue', value: (v) => Number(v.notDue), width: 14 },
      { header: '1-30', value: (v) => Number(v.d1_30), width: 14 },
      { header: '31-60', value: (v) => Number(v.d31_60), width: 14 },
      { header: '61-90', value: (v) => Number(v.d61_90), width: 14 },
      { header: '90+', value: (v) => Number(v.over90), width: 14 },
      { header: 'total', value: (v) => Number(v.total), width: 14 },
    ];
    await exportRows(
      `อายุหนี้เจ้าหนี้_${companyCode}_${todayStamp()}.xlsx`,
      'อายุหนี้เจ้าหนี้',
      cols,
      vendors,
    );
  }

  return (
    <div className="tbl-shell">
      <div className="filter-row" style={{ justifyContent: 'space-between' }}>
        <div className="muted" style={{ fontSize: 13 }}>
          ณ วันที่ {aging ? fmtDate(aging.asOfDate) : '—'}
        </div>
        <button className="btn" onClick={() => void handleExport()} disabled={vendors.length === 0}>
          <Icon name="download" size={14} /> ส่งออก
        </button>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 480 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>ผู้ขาย</th>
              <th className="num">ยังไม่ครบกำหนด</th>
              <th className="num">1-30 วัน</th>
              <th className="num">31-60 วัน</th>
              <th className="num">61-90 วัน</th>
              <th className="num">90+ วัน</th>
              <th className="num">รวม</th>
            </tr>
          </thead>
          <tbody>
            {vendors.length === 0 && (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มียอดค้างจ่าย
                </td>
              </tr>
            )}
            {vendors.map((v) => (
              <tr key={v.vendorId}>
                <td>{v.vendorName}</td>
                <td className="num tnum">{fmt(toNumber(v.notDue), 2)}</td>
                <td className="num tnum">{fmt(toNumber(v.d1_30), 2)}</td>
                <td className="num tnum">{fmt(toNumber(v.d31_60), 2)}</td>
                <td className="num tnum">{fmt(toNumber(v.d61_90), 2)}</td>
                <td className="num tnum">{fmt(toNumber(v.over90), 2)}</td>
                <td className="num tnum">
                  <b>{fmt(toNumber(v.total), 2)}</b>
                </td>
              </tr>
            ))}
            {aging && vendors.length > 0 && (
              <tr className="total">
                <td>รวมทั้งสิ้น</td>
                <td className="num tnum">{fmt(toNumber(aging.totals.notDue), 2)}</td>
                <td className="num tnum">{fmt(toNumber(aging.totals.d1_30), 2)}</td>
                <td className="num tnum">{fmt(toNumber(aging.totals.d31_60), 2)}</td>
                <td className="num tnum">{fmt(toNumber(aging.totals.d61_90), 2)}</td>
                <td className="num tnum">{fmt(toNumber(aging.totals.over90), 2)}</td>
                <td className="num tnum">
                  <b>{fmt(toNumber(aging.grandTotal), 2)}</b>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
